import React from "react";
import { Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  getModuleVisual,
  moduleAccentColor,
} from "../../constants/moduleVisuals";

const SIZES = {
  xs: { box: 20, icon: 13, radius: "6px" },
  sm: { box: 26, icon: 16, radius: "7px" },
  md: { box: 34, icon: 20, radius: "9px" },
  lg: { box: 44, icon: 26, radius: "12px" },
};

/**
 * Props that put a module's badge before a MUI <Tab>'s label - spread onto
 * the Tab. Empty when no key is given, so plain tabs are left as they were.
 */
export const moduleTabProps = (moduleKey) =>
  moduleKey
    ? {
        icon: <ModuleIconBadge moduleKey={moduleKey} size="xs" />,
        iconPosition: "start",
        sx: { gap: 0.75, "& .MuiTab-iconWrapper": { mr: 0 } },
      }
    : {};

/**
 * A module's icon in a rounded, tinted square of its accent colour (see
 * constants/moduleVisuals.js). On the night theme the badge picks up a soft
 * glow in the same colour.
 */
export default function ModuleIconBadge({ moduleKey, size = "sm", sx }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { icon: Icon } = getModuleVisual(moduleKey);
  const color = moduleAccentColor(moduleKey, theme.palette.mode);
  const { box, icon, radius } = SIZES[size] ?? SIZES.sm;

  return (
    <Box
      component="span"
      sx={{
        width: box,
        height: box,
        borderRadius: radius,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color,
        bgcolor: alpha(color, isDark ? 0.16 : 0.1),
        boxShadow: isDark
          ? `inset 0 0 0 1px ${alpha(color, 0.35)}, 0 0 10px ${alpha(color, 0.22)}`
          : `inset 0 0 0 1px ${alpha(color, 0.18)}`,
        // Hosts such as TreeItem's icon slot size every nested svg (18px);
        // the glyph has to keep the size that fits its badge.
        "& svg": { fontSize: `${icon}px !important` },
        ...sx,
      }}
    >
      <Icon />
    </Box>
  );
}
